/* eslint-disable max-lines */
import {Time, Util} from 'yougile-util'
import {
  DataObjectId,
  Hub,
  isDataObjectId,
  noBoardContainerId,
  SandboxTree
} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {Sync} from '@client/sync'
import {SyncEvents, SyncUpdate} from '@client/sync-events'
import {Board} from '@extensions/board'
import {ChatUtils} from '@extensions/chat-utils'
import {Column} from '@extensions/column'
import {GanttDraft} from '@extensions/gantt-draft'
import {Project} from '@extensions/project'
import {Sticker, stickerCtl} from '@extensions/sticker/sticker'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task} from '@extensions/task'
import {Items} from './items'
import {UIElement} from './ui-element'

export type ItemType =
  | 'Company'
  | 'Project'
  | 'Board'
  | 'Column'
  | 'Task'
  | 'Subtask'

export type ItemGetUi = (
  companyId: DataObjectId,
  itemId: DataObjectId
) => UIElement

export type ItemOpen = (
  itemId: DataObjectId,
  type: ItemType,
  findHub: (id: DataObjectId) => Hub
) => void

const itemTypes: ItemType[] = [
  'Company',
  'Project',
  'Board',
  'Column',
  'Task',
  'Subtask'
]

export type TaskLocation = {
  column?: DataObjectId
  board?: DataObjectId
  project?: DataObjectId
}

/**
 * @name=item variable
 * @code
 * Items.get('... id');
 * @description
 * Variable representing some YouGile object (task, column, board or project).
 * Can be get by calling Items.get(...) (or by using Object navigator).
 * @descriptionRu
 * Переменная представляющая задачу, колонку, доску или проект.
 * Может быть получена вызовом функции Items.get(...)
 * (или через Навигатор по объектам).
 * @example
 * var x = Items.get('... object id here');
 * // variable “x” is an item variable,
 * // and we can access all its properties
 * Notifier.success('Object name is ' + x.name);
 *
 * // x is either project, board, column or task and you can find out it
 * // by accessing a type property (x.type), possible values are: Task, Column,
 * // Board, Project, Company
 * if (x.type === 'Task') {
 *   // do something if “x” is a task
 * }
 *
 * // any object is inside some hierarchical structure: tasks are inside
 * // columns, columns are inside boards, boards inside projects and projects
 * // inside Company.
 * // You can access a parent object by calling .up()
 * x.up(); // gets the parent object which is also an item variable
 * x.up().name; // the name of parent object
 * // and we can also do this
 * x.up().up().name; // the name of objects grandparent
 *
 * // to get all the child objects of some object .list() method is used
 * x.list(); // if object is a column then we get here array of its tasks
 *
 * // if you want to get all tasks inside a board then you can write
 * if (x.type === 'Board') {
 *   var all = [];
 *   x.list().forEach(function (col) { // for each column inside x
 *     col.list().forEach(function (t) { // for each task inside col
 *       // adding task “t” to the “all” array
 *       all.push(t);
 *     });
 *   });
 *   // now “all” variable contains all tasks inside board “x”
 * }
 */
export class Item {
  private static getUi: ItemGetUi
  private static open: ItemOpen

  public id: DataObjectId
  public box: SandboxTree /* api:exclude */

  constructor(id: DataObjectId, box: SandboxTree) {
    this.id = id
    this.box = box
  }

  public static init(getUi: ItemGetUi, open: ItemOpen) {
    Item.getUi = getUi
    Item.open = open
  }

  /**
   * @code
   * Items.get('... object id').type;
   * @description
   * Returns the string with a type of the object,
   * available types are: 'Task', 'Subtask',
   * 'Column', 'Board', 'Project'. Readonly.
   * @descriptionRu
   * Возвращает строку с типом объекта. Доступные типы: 'Task', 'Subtask',
   * 'Column', 'Board', 'Project'. Только для чтения.
   */
  public get type(): ItemType {
    const entity = this.box.get(this.id)
    const parentHubs = this.box.getAllParentHubs(entity)

    if (entity?.location === noBoardContainerId) {
      return SubtaskUtil.isSubtask(entity.id) ? 'Subtask' : 'Task'
    }

    const level = parentHubs.length
    if (level >= itemTypes.length) {
      return 'Subtask'
    } else {
      return itemTypes[level]
    }
  }

  /**
   * @code
   * Items.get('... object id').name;
   * @description
   * Returns a name of the object. Name can be changed by assigning to
   * this property.
   * @descriptionRu
   * Возвращает имя объекта.
   * @example
   * var task = Items.get('...');
   * task.name = task.name + '!';
   */
  public get name(): string {
    const entity = this.box.get(this.id)
    if (entity.dataType === 'User') {
      return Accounts.get(entity.id).realName
    } else {
      return entity.title.split('___deleted')[0]
    }
  }

  public set name(value: string) {
    const hub = this.box.get(this.id) as Hub
    if (hub.dataType === 'User') {
      throw new Error('Changing user name is disallowed')
    }
    this.box.editTitle(hub, value)
  }

  /**
   * @code
   * Items.get('... object id').deleted;
   * @description
   * Returns true if the object is deleted. You can assign a value to this
   * property.
   * @descriptionRu
   * Возвращает true если объект удалён. Вы можете назначить
   * значение этому свойству.
   */
  public get deleted(): boolean {
    return this.box.get(this.id).deleted
  }

  public set deleted(value: boolean) {
    const hub = this.box.get(this.id) as Hub
    if (hub.dataType === 'User') {
      throw new Error('Deleting users from scripts is disallowed')
    }

    if (value) {
      this.box.remove(hub)
    } else {
      this.box.restore(hub)
    }

    GanttDraft.planEntitySubtree(hub.id, this.box)
  }

  /**
   * @code
   * Items.get('... object id').timestamp;
   * @description
   * Returns unix timestamp of the creation time of the object. Readonly.
   * @descriptionRu
   * Возвращает unix timestamp даты создания объекта. Только чтение.
   */
  public get timestamp(): number {
    return this.box.get(this.id).timestamp
  }

  /**
   * @code
   * Items.get('... object id').timestamp;
   * @example
   * var task = Items.get('... some task id');
   * task.path;
   * // returns:
   * // 'My company » Example project » First board » Main column'
   * @description
   * Returns a human readable path for the object.
   * @descriptionRu
   * Возвращает форматированный путь к объекту.
   */
  public get path(): string {
    return ChatUtils.getChatLocation(this.box.get(this.id) as Hub)
  }

  /**
   * @code
   * Items.get('... object id').ui;
   * @example
   * var task = Items.get('... some task id');
   * task.ui.style = {
   *   background: 'red'
   * };
   * @description
   * UI element corresponding to the object
   * (see UIElement for details).
   * @descriptionRu
   * UI элемент, соответсвующий объекту
   * (cм. UIElement).
   */
  public get ui(): UIElement {
    return Item.getUi(this.box.companyId, this.id)
  }

  /**
   * @code
   * Items.get('... object id').open();
   * @example
   * // open “My project” project
   * Company.find('My project').open();
   * @description
   * Navigate user to the object (project or board). If it is called from task
   * or column then the containing board is opened.
   * @descriptionRu
   * Перемещение пользователя к объекту (проекту или доске). Если
   * вызывается из задачи или колонки, открывается содержащая их
   * доска.
   */
  public open(): void {
    Item.open(this.id, this.type, (id: DataObjectId) => this.box.get(id) as Hub)
  }

  /**
   * @code
   * Items.get('... object id').move(Items.get('??? new parent id'));
   * @description
   * Moves the object to a new parent (for example you can move a task
   * to another column).
   * @descriptionRu
   * Перемещает объект к новому родителю (например, вы можете
   * переместить задачу в другую колонку).
   */
  public move(newParent: Item): void {
    const hub = this.box.get(this.id) as Hub
    if (hub.dataType === 'User') {
      throw new Error('Moving users is disallowed')
    }
    this.box.moveTreeNode(hub, hub.location, newParent.id, true, false)
  }

  /**
   * @code
   * Items.get('... object id').find('... name');
   * @description
   * Gets a child object of the object with the specified name.
   * @descriptionRu
   * Получает дочерний объект объекта с определенным именем.
   */
  public find(name?: string): Item | undefined {
    if (!name) {
      return undefined
    }
    const child = this.box.findHubByTitle(name, this.id, true)
    return child ? new Item(child.id, this.box) : undefined
  }

  /**
   * @code
   * Items.get('... object id').up();
   * @example
   * // getting a task
   * var t = Items.get('... task id here');
   * // column
   * var col = t.up();
   * // board
   * var b = col.up();
   * Notifier.success('The task ' + t.name + ' is inside board ' + b.name);
   * @description
   * Returns parent object of the specified object.
   * @descriptionRu
   * Возвращает родительский объект указанного объекта.
   */
  public up(): Item | undefined {
    if (this.id === this.box.companyId) {
      return undefined
    }
    const hub = this.box.get(this.id) as Hub
    return new Item(hub.location, this.box)
  }

  /**
   * @code
   * Items.get('... object id').list();
   * @example
   * // getting the board
   * var b = Items.get('... board’s id');
   * b.list(); // array of columns inside the board
   * @description
   * Returns an array of children objects for the object.
   * @descriptionRu
   * Возвращает массив дочерних объектов указанного объекта.
   */
  public list(): Item[] {
    return this.box
      .getChildrenHubs(this.id)
      .map((h: Hub) => new Item(h.id, this.box))
  }

  /**
   * @code
   * Items.get('... object id').add('... name');
   * @example
   * // getting a column
   * var c = Items.get('... column id here');
   * c.add('Some new task!');
   *
   * // we can also save a new object to a variable to
   * // work with it later
   * var t2 = c.add('Another task');
   * t2.name = 'Name changed';
   * @description
   * Adds a new child object with the specified name.
   * @descriptionRu
   * Добавляет новый дочерний объект указанного объекта.
   */
  public add(name: string): Item {
    const hub = this.box.get(this.id) as Hub
    const toItem = (newHub: Hub) => new Item(newHub.id, this.box)
    switch (this.type) {
      case 'Company':
        return toItem(Project.createNew(this.box.companyId, name, false))
      case 'Project':
        return toItem(Project.addBoard(name, hub, false))
      case 'Board':
        return toItem(Board.addColumn(name, hub))
      case 'Column':
        return toItem(
          Task.createTask(name, {location: hub.id, companyId: hub.companyId})
        )
      default:
        return toItem(
          Task.createTask(name, {location: hub.id, companyId: hub.companyId})
        )
    }
  }

  /**
   * @code
   * Items.get('... object id').getData('key');
   *
   * @example
   * var t = Items.get('... task id');
   * var specificData = t.getData('clientId');
   * console.log(specificData); // '9023929875'
   *
   * @description
   * Gets specific custom data attached to the object by the given key.
   *
   * @descriptionRu
   * Получает пользовательские данные, прикреплённые к объекту, по указанному ключу.
   *
   * @param {string} key - The key to retrieve specific data.
   * @returns {unknown} - The requested data or `undefined` if the key is not found.
   */
  public getData(key: string): unknown {
    const hub = this.box.get(this.id) as Hub
    if (key) {
      return hub.apiData?.[key] ? Util.clone(hub.apiData[key]) : undefined
    }
    return hub.apiData ? Util.clone(hub.apiData) : {}
  }

  /**
   * @code
   * Items.get('... object id').setData('key', {...});
   * @example
   * var t = Items.get('... task id');
   * var d = {
   *   clientId: '9023929875'
   *   contact: {
   *     phone: '+1234567',
   *     email: 'some@example.com'
   *   }
   * };
   * t.setData('client-info', d);
   * // this data is now saved on server and can be accessed
   * // from scripts for all users.
   *
   * // modification existent data example
   * var data = t.getData('order');
   * data.orderNum = 2341;
   * t.setData('order', data);
   * @description
   * Custom data can be attached to any YouGile object
   * (JSON). This data is automatically
   * synchronized with server and all users and if available
   * with getData function.
   * @descriptionRu
   * К любому объекту YouGile можно добавить пользовательские
   * данные (JSON). Они будут автоматически синхронизированы
   * с сервером и всеми юзерами и доступны через
   * функцию getData.
   */
  public setData(key: string, json: unknown): void {
    const hub = this.box.get(this.id) as Hub

    if (arguments.length > 1) {
      if (typeof key !== 'string') {
        throw new Error('Key must be a string')
      }
      if (
        !hub.apiData ||
        typeof hub.apiData !== 'object' ||
        Array.isArray(hub.apiData)
      ) {
        hub.apiData = {}
      }
      hub.apiData[key] = json ? Util.clone(json) : undefined
    } else {
      hub.apiData = key ? Util.clone(key) : {}
    }

    hub.commit()
  }

  /**
   * @code
   * Items.get('...').triggerUpdate(false);
   * @description
   * Tells the system that the item should be considered as updated and
   * should be redrawn.
   * @descriptionRu
   * Принудительно вызвать событие обновления объекта
   * (система перерисует всё что связано с объектом и выполнит
   * функцию Items.onUpdate, если значение параметра false - вызовется для текущего клиента,
   * если значение true - то для всех клиентов).
   */
  public triggerUpdate(allUsers = false): void {
    const ids = new Set([this.id])
    const parent = this.up()
    if (parent) {
      ids.add(parent.id)
    }

    SyncEvents.triggerUpdate(
      this.box.companyId,
      [new SyncUpdate(false, this.box.companyId, [...ids])],
      false
    )

    if (allUsers) {
      for (const id of ids) {
        const h = this.box.getHub(id)
        if (h?.commit) {
          h.data ??= {}
          h.data.updatedByScript = Time.now()
          h.commit()
        }
      }
    }
  }

  /**
   * @code
   * Items.get('... task id').isArchived();
   * @description
   * Returns true is the object is archived.
   * @descriptionRu
   * Возвращает true если объект помещён в архив.
   * @example
   * const task = Items.get('...');
   * const completed = task.isArchived();
   */
  public isArchived(): boolean {
    const hub = this.box.get(this.id) as Hub
    return Task.isArchived(hub, this.box.companyId)
  }

  /**
   * @code
   * Items.get('... task id').isCompleted();
   * @description
   * Returns true is the object is a completed task.
   * @descriptionRu
   * Возвращает true если объект — это выполненная задача.
   * @example
   * const task = Items.get('...');
   * const completed = task.isArchived();
   */
  public isCompleted(): boolean {
    if (this.type !== 'Task') {
      return false
    }
    const hub = this.box.get(this.id) as Hub
    return Task.getGTDStage(hub) === 'done'
  }

  /**
   * @code
   * Items.get('... task id').setCompleted(true);
   * @description
   * Completes or uncompletes the task.
   * @descriptionRu
   * Выполняет задачу (либо снимает выполненность, если значение параметра false).
   * @example
   * const task = Items.get('...');
   * task.setCompleted();
   */
  public setCompleted(value: boolean): void {
    if (!['Subtask', 'Task'].includes(this.type)) {
      return
    }
    const hub = this.box.get(this.id) as Hub
    Task.changeCompleted(hub, value)
  }

  /**
   * @code
   * Items.get('... task id').getAllParentTasks();
   * @description
   * Returns an array of all parent tasks.
   * @descriptionRu
   * Возвращает массив всех родительских задач.
   * @example
   * const task = Items.get('...');
   * task.getAllParentTasks();
   */
  public getAllParentTasks(force?: boolean): Item[] | undefined {
    if (!['Subtask', 'Task'].includes(this.type)) {
      return undefined
    }
    const hub = this.box.get(this.id) as Hub

    return SubtaskUtil.getParentTaskIds(hub.id, force).map(Items.get)
  }

  /**
   * @code
   * Items.get('... task id').getTaskLocations();
   * @description
   * Returns task locations - board, column, project.
   * @descriptionRu
   * Возвращает расположение задачи - доска, колонка, проект.
   * @example
   * const task = Items.get('...');
   * task.getTaskLocations();
   */
  public getTaskLocations(): TaskLocation | undefined {
    if (this.type !== 'Task') {
      return undefined
    }

    const locations = Task.getLocations(this.id)

    return {
      column: locations?.column?.id,
      board: locations?.board?.id,
      project: locations?.project?.id
    }
  }

  /**
   * @code
   * Items.get('... task id').getAssignedUsers();
   * @description
   * Returns assigned users.
   * @descriptionRu
   * Возвращает пользователей, назначенных задаче.
   * @example
   * const task = Items.get('...');
   * task.getAssignedUsers();
   */
  public getAssignedUsers(): DataObjectId[] | undefined {
    if (!['Subtask', 'Task'].includes(this.type)) {
      return undefined
    }
    const hub = this.box.get(this.id) as Hub

    return Sticker.getUsers(hub)
  }

  /**
   * @code
   * Items.get('... task id').setAssignedUsers();
   * @description
   * Sets assigned users.
   * @descriptionRu
   * Устанавливает пользователей, назначенных задаче.
   * @example
   * const task = Items.get('...');
   * task.setAssignedUsers();
   */
  public setAssignedUsers(users: DataObjectId[]): void {
    if (!['Subtask', 'Task'].includes(this.type)) {
      return
    }
    const hub = this.box.get(this.id) as Hub
    const box = Sync.get(hub)
    const sticker = stickerCtl.User.getSticker(box)
    const preparedUsers = users.filter(userId => isDataObjectId(userId))
    Sticker.pin(sticker, hub.id, hub, preparedUsers)
  }

  public isTaskCompleted(): boolean {
    if (this.type !== 'Task') {
      return false
    }
    const hub = this.box.get(this.id) as Hub
    return Task.isArchived(hub, this.box.companyId)
  }

  /**
   * @code
   * Items.get(''... task id).setTaskCompleted(true);
   * @description
   * Allows to change archiveness of the task. If the object
   * is not a task then this function has no effect.
   * @descriptionRu
   * Позволяет менять состояние архивированности задачи. Если объект
   * не является задачей, то ничего не происходит при вызове этой функции.
   * @example
   * const task = Items.get('...');
   * const completed = task.isTaskCompleted();
   * // changing completeness state of the task
   * task.setTaskCompleted(!completed);
   */
  public setTaskCompleted(value: boolean): void {
    if (this.type !== 'Task') {
      return
    }
    const hub = this.box.get(this.id) as Hub
    if (this.isTaskCompleted() !== !!value) {
      void Task.changeArchived(hub, !!value)
    }
  }

  /**
   * @code
   * Items.get('... task id').getTaskCompletionTime();
   * @description
   * Returns time of task completion (Unix timestamp).
   * @descriptionRu
   * Возвращает время архивирования задачи (Unix timestamp).
   * @example
   * const task = Items.get('...');
   * const completed = task.getTaskCompletionTime();
   */
  public getTaskCompletionTime(): number {
    if (this.type !== 'Task') {
      return undefined
    }
    const hub = this.box.get(this.id) as Hub
    return hub.complete
  }

  /**
   * @code
   * Items.get('... column id').setMirror([]);
   * @description
   * Sets a column as a mirror for another columns.
   * @descriptionRu
   * Сделать колонку зеркалом других колонок.
   * @example
   * var column = Items.get('...');
   * var board = Items.get('...');
   * // making “column” to be a mirror
   * // of all columns inside “board”
   * column.setMirror(board.list());
   *
   * // making the column to be an ordinary
   * // column again (not a mirror)
   * column.setMirror([]);
   */
  public setMirror(columns: Item[]): void {
    columns = columns || []
    Column.setMirror(Items.toHub(this), columns.map(Items.toHub))
  }

  /**
   * @code
   * Items.get('... column id').isMirror();
   * @description
   * Returns true is this is a mirror colun.
   * @descriptionRu
   * Возвращает true если объект являетя зеркальной колонкой.
   * @example
   * var column = Items.get('...');
   * column.isMirror();
   */
  public isMirror(): boolean {
    const obj = this.box.get(this.id)
    return this.type === 'Column' && Column.isMirror(obj) && !this.isReport()
  }

  /**
   * @code
   * Items.get('... column id').isReport();
   * @description
   * Returns true is this is a report colun.
   * @descriptionRu
   * Возвращает true если объект являетя колонкой с отчётом.
   * @example
   * var column = Items.get('...');
   * column.isReport();
   */
  public isReport(): boolean {
    const obj = this.box.get(this.id) as Hub
    return this.type === 'Column' && !!obj?.data?.report
  }

  /**
   * @code
   * Items.get('... column id').listMirrored();
   * @description
   * Returns an array of mirrored columns.
   * @descriptionRu
   * Возвращает массив отзеркалированных колонок.
   * @example
   * var column = Items.get('...');
   * var mirrored = column.listMirrored();
   * // checking if the column is mirror
   * if (mirrored.length > 0) {
   *  // column is a mirror for some other columns
   * }
   */
  public listMirrored(): Item[] {
    const column = Items.toHub(this)
    return Column.isMirror(column)
      ? Column.getMirroredColumns(column).map(Items.fromHub)
      : []
  }
}
